import {
    EdgeAndBaseLineDetector,
} from '@src/slide/application/service/snap-line-service/detector/edge-and-base-line';
import { MarginDetector } from '@src/slide/application/service/snap-line-service/detector/margin';
import { RectDetector } from '@src/slide/application/service/snap-line-service/detector/rect';
import { isCloseEqual } from '@src/slide/application/service/snap-line-service/util';
import {
    DYNAMIC_REFERS_LIMIT,
} from '@src/slide/application/service/snap-line-service/constant';
import { shouldDisplayDebugContent } from '@src/slide/application/service/snap-line-service/util/debug';
import type { IActiveSnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { SnapLineContext } from '@src/slide/application/service/snap-line-service/context';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';

export class SnapLineDetector {
    private edgeAndBaseLineDetector: EdgeAndBaseLineDetector;
    private marginDetector: MarginDetector;
    private rectDetector: RectDetector;

    public constructor(
        public selectionService: ISelectionService,
        public uiDataService: IUiDataService,
        private context: SnapLineContext,
    ) {
        this.edgeAndBaseLineDetector = new EdgeAndBaseLineDetector(this.context);
        this.marginDetector = new MarginDetector(this.context);
        // rect 探测比较特殊，refGrids都是根据当前位置生成的。所以不适用缓存
        this.rectDetector = new RectDetector(this.context, true);
    }

    public detect() {
        // 开启测试线，辅助排查问题
        this.setDevLines();

        /**
         * 在超过动态参照物上限的时候，禁用动态探测
         *     在元素很多的情况下，已经做了大小限制过滤。
         *     如果还有非常多动态参照物的情况有两种：1. 元素都相对较小 2. 元素都重叠在一起
         *     上述情况在动态探测（间距、对称、宽高）意义都是相对较小的，而且会造成页面卡顿
         * 目前进行功能降级，后续有更合适的算法来计算参与探测的参照物后再打开这里
         */
        return this.processActiveSnapLines(
            this.edgeAndBaseLineDetector.detect(),
            this.shouldDisableDynamicDetect() ? [] : this.marginDetector.detect(),
            this.shouldDisableDynamicDetect() ? [] : this.rectDetector.detect(),
        );
    }

    public reset() {
        this.edgeAndBaseLineDetector.clearCache();
        this.marginDetector.clearCache();
        this.rectDetector.clearCache();
    }

    private shouldDisableDynamicDetect() {
        return this.context.dynamicRefer.refs.length > DYNAMIC_REFERS_LIMIT;
    }
    /**
     * 根据预测的参考线，根据所有不同类型的参考线，综合判定最终生效的参考线
     * 暂无规则，为后续添加过滤条件做一个基础
     */
    private processActiveSnapLines(
        edgeAndBaseLineSnapLines: IActiveSnapGridLine[] = [],
        marginSnapLines: IActiveSnapGridLine[] = [],
        rectSnapLines: IActiveSnapGridLine[] = [],
    ) {
        const activeSnapLines = [
            ...edgeAndBaseLineSnapLines,
            ...marginSnapLines,
            ...rectSnapLines,
        ];

        return this.handleActiveSnapLinesConflict(activeSnapLines);
    }

    /**
     * 吸附线冲突处理
     */
    private handleActiveSnapLinesConflict(activeSnapLines: IActiveSnapGridLine[]): IActiveSnapGridLine[] {
        let finalActiveSnapLines: IActiveSnapGridLine[] = [];

        activeSnapLines.forEach((snapLine) => {
            const sameOrientActiveSnapLineIndex = finalActiveSnapLines
                .findIndex(line => line.orient === snapLine.orient);

            if (sameOrientActiveSnapLineIndex === -1) {
                finalActiveSnapLines.push(snapLine);
                return;
            }

            const sameOrientActiveSnapLine = finalActiveSnapLines[sameOrientActiveSnapLineIndex];
            // 如果位置一样，就都保留
            if (isCloseEqual(sameOrientActiveSnapLine.activeAdsorbGap, snapLine.activeAdsorbGap)) {
                finalActiveSnapLines.push(snapLine);
            } else {
                // 如果位置冲突，就保留吸附距离更小的
                const isCurrentSnapLineCloser = Math.abs(snapLine.activeAdsorbGap)
                    < Math.abs(sameOrientActiveSnapLine.activeAdsorbGap);
                if (isCurrentSnapLineCloser) {
                    // 同方向的activeSnapLines 距离都相等。如果有更新的，就要吧将同方向的都过滤掉，用更近的代替
                    finalActiveSnapLines = finalActiveSnapLines
                        .filter(snapLine => snapLine.orient !== sameOrientActiveSnapLine.orient);
                    finalActiveSnapLines.push(snapLine);
                }
            }
        });

        return finalActiveSnapLines;
    }

    private setDevLines() {
        if (shouldDisplayDebugContent()) {
            this.context.elementGrids = [
                // @ts-ignore
                ...this.edgeAndBaseLineDetector.getRefGrids(),
                // @ts-ignore
                ...this.marginDetector.getRefGrids(),
                // @ts-ignore
                // ...this.rectDetector.getRefGrids(),
            ];
            this.context.detectGrids = [
                // @ts-ignore
                ...this.edgeAndBaseLineDetector.getDetectGrids(),
                // @ts-ignore
                ...this.marginDetector.getDetectGrids(),
                // @ts-ignore
                ...this.rectDetector.getDetectGrids(),
            ];
        }
    }
}
